import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { isValid, parse } from "date-fns";
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Grid,
  Icon,
  Message,
  Modal,
  Popup,
  Segment,
  Select,
} from "semantic-ui-react";
import uuid from "uuid";

import { formatDate } from "simplydo/core/dates";
import { removeLocalStorageItem } from "simplydo/core/helpers";
import type { OpenAPI } from "simplydo/interfaces";
import {
  LabelChooser,
  LabelItem,
} from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";

import api from "api";
import util from "utils/utils";

import {
  availableConditions,
  conditionChecks,
  getDefaultCondition,
  renderValueCheck,
} from "components/ideas/Body/Custom/fieldCondition";
import { DateInput } from "components/lib/DateInputs";
import { RadioHeader, TooltipButton } from "components/lib/UI";

import AnnouncementBase from "./Base";

type Announcement =
  OpenAPI.GET<"/{entity}/{id}/announcements">["response"]["automaticAnnouncements"][number];

type AnnouncementModalProps = {
  forType: string;
  forId: string;
  standardConditions: any[];
  automaticConditions?: any[];
  standardConditionsRequired: boolean;
  scheduledAnnouncements: Announcement[];
  announcement: Announcement;
  deleteAnnouncement: (id: string, callback: () => void) => void;
  setAnnouncement: (announcement: Announcement) => void;
  setScheduledAnnouncements: (announcements: Announcement[]) => void;
  automaticAnnouncements: Announcement[];
  setAutomaticAnnouncements: (announcements: Announcement[]) => void;
  copyToNew?: (announcement: Announcement) => void;
  enabledAnnouncementTypes?: string[];
  setEmailPreviewOpen: (open: boolean) => void;
};

const AnnouncementModal = ({
  forType,
  forId,
  standardConditions,
  standardConditionsRequired,
  scheduledAnnouncements,
  setScheduledAnnouncements,
  announcement,
  setAnnouncement,
  deleteAnnouncement,
  automaticConditions,
  setAutomaticAnnouncements,
  automaticAnnouncements,
  copyToNew,
  setEmailPreviewOpen,
  enabledAnnouncementTypes = ["standard", "automatic"],
}: AnnouncementModalProps) => {
  const [recipientsMatched, setRecipientsMatched] = useState(0);
  const [subconditionOptions, setSubconditionOptions] = useState({});
  const [sending, setSending] = useState(false);
  const { t } = useTranslation();

  const addCondition = useCallback(() => {
    setAnnouncement((prevAnn) => ({
      ...prevAnn,
      recipientConditions: [
        ...(prevAnn.recipientConditions ?? []),
        { type: null, data: null, key: uuid.v4() },
      ],
    }));
  }, [setAnnouncement]);

  const getStandardSubconditions = useCallback(
    (standardCondition) =>
      standardConditions.find((sc) => sc.value === standardCondition?.type)
        ?.subConditions,
    [standardConditions],
  );
  const getAutomaticSubconditions = useCallback(
    (condition) =>
      automaticConditions.find((sc) => sc.value === condition)?.subConditions,
    [automaticConditions],
  );
  const subconditions =
    announcement.type === "automatic" ?
      getAutomaticSubconditions(announcement.sendWhen)
    : getStandardSubconditions(announcement.sendWhen);

  const updateCondition = useCallback(
    (conditionIndex, label, value) => {
      const updatedRecipientConditions = [...announcement.recipientConditions];
      updatedRecipientConditions[conditionIndex][label] = value;
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  const deleteCondition = useCallback(
    (conditionIndex) => {
      const updatedRecipientConditions = [...announcement.recipientConditions];
      updatedRecipientConditions.splice(conditionIndex, 1);
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  const updateSubconditionOptions = useCallback(
    (conditionKey, option, selectSingle) => {
      const updatedRecipientConditions = announcement.recipientConditions.map(
        (condition) => {
          if (condition.key !== conditionKey) {
            return condition;
          }
          if (selectSingle) {
            return { ...condition, data: option };
          }

          const isAdding = !condition.data || !condition.data.includes(option);
          if (isAdding) {
            return { ...condition, data: [...(condition.data || []), option] };
          }
          return {
            ...condition,
            data: condition.data.filter((e) => e !== option),
          };
        },
      );
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  useEffect(() => {
    let fetching = true;
    api.announcements.getAudience(
      forType,
      forId,
      {
        type: "standard",
        recipientConditions: announcement.recipientConditions,
      },
      ({ recipientsMatched: response }) => {
        if (!fetching) return;
        setRecipientsMatched(response);
      },
      (err) => {
        if (!fetching) return;
        toast.error(err.message);
        setRecipientsMatched(0);
      },
    );

    return () => {
      fetching = false;
    };
  }, [forType, forId, announcement.recipientConditions]);

  const createAnnouncement = useCallback(() => {
    setSending(true);

    if (announcement.type === "standard" && announcement.sendAt) {
      announcement.type = "scheduled";
    }

    const {
      subject,
      body,
      text,
      replyTo,
      callToActionText,
      callToActionLink,
      recipientConditions,
      calendarEvent,
      attachments,
      type,
      sendAt,
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
      teamsMeeting,
    } = announcement;

    const automaticFields = {
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
    };
    const scheduledFields = { recipientConditions, sendAt };

    const standardFields = { recipientConditions };
    api.announcements.create(
      forType,
      forId,
      {
        type,
        subject,
        body,
        text,
        replyTo,
        callToActionText,
        callToActionLink,
        calendarEvent,
        attachments,
        teamsMeeting,
        ...(type === "automatic" ? automaticFields : {}),
        ...(type === "scheduled" ? scheduledFields : {}),
        ...(type === "standard" ? standardFields : {}),
      },
      (announcement) => {
        toast.success(
          announcement.type === "standard" ?
            t("announcements.sent", {
              audience: `${announcement.recipientsMatched} recipients`,
            })
          : t("announcements.created"),
        );
        setSending(false);
        setAnnouncement({});
        setRecipientsMatched(0);
        removeLocalStorageItem(`announcement-${forId}`);
        if (announcement.type === "automatic") {
          setAutomaticAnnouncements((prev) => [...prev, announcement]);
        } else if (announcement.type === "scheduled") {
          setScheduledAnnouncements((prev) => [...prev, announcement]);
        }
      },
      (err) => {
        setSending(false);
        toast.error(err.message);
      },
    );
  }, [
    forType,
    forId,
    announcement,
    setAnnouncement,
    setScheduledAnnouncements,
    setAutomaticAnnouncements,
    t,
  ]);

  const updateAnnouncement = useCallback(() => {
    setSending(true);
    const {
      subject,
      body,
      text,
      replyTo,
      callToActionText,
      callToActionLink,
      recipientConditions,
      calendarEvent,
      attachments,
      type,
      sendAt,
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
      teamsMeeting,
    } = announcement;

    const automaticFields = {
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
    };
    const standardFields = { recipientConditions };
    const scheduledFields = {
      recipientConditions,
      ...(sendAt && isValid(new Date(sendAt)) ?
        { sendAt: new Date(sendAt).toISOString() }
      : {}),
    };
    api.announcements.update(
      forType,
      forId,
      announcement._id,
      {
        subject,
        body,
        text,
        sendWhen,
        replyTo,
        callToActionText,
        callToActionLink,
        calendarEvent,
        attachments,
        teamsMeeting,
        ...(type === "automatic" ? automaticFields : {}),
        ...(type === "scheduled" ? scheduledFields : {}),
        ...(type === "standard" ? standardFields : {}),
      },
      (announcement) => {
        if (announcement.type === "automatic") {
          const updatedAnnouncements = Object.assign(
            [],
            automaticAnnouncements,
          ).map((a) => {
            if (a._id === announcement._id) return { ...announcement };
            return a;
          });
          setAutomaticAnnouncements(updatedAnnouncements);
        } else {
          const updatedAnnouncements = Object.assign(
            [],
            scheduledAnnouncements,
          ).map((a) => {
            if (a._id === announcement._id) return { ...announcement };
            return a;
          });
          setScheduledAnnouncements(updatedAnnouncements);
        }
        // Need to invalidate board lane query to reflect changes to subject in the move-to-lane announcement type
        if (forType === "challenge" && announcement.type === "automatic") {
          api.invalidateQueries(api.boards.getProjectLanes, [forId]);
        }
        setSending(false);
        setAnnouncement({});
        removeLocalStorageItem(`announcement-${forId}`);
        toast.success(t("generic.typeSaved", { type: "Announcement" }));
      },
      (err) => {
        toast.error(err.message);
        setSending(false);
      },
    );
  }, [
    t,
    forType,
    forId,
    announcement,
    scheduledAnnouncements,
    setScheduledAnnouncements,
    setAnnouncement,
    automaticAnnouncements,
    setAutomaticAnnouncements,
  ]);

  const { recipientConditions = [] } = announcement;

  useEffect(() => {
    if (announcement.type === "automatic") {
      const cond = automaticConditions.find(
        (sc) => sc.value === announcement.sendWhen,
      );
      if (cond?.subConditions) {
        cond.subConditions.forEach((subcondition) => {
          if (
            subcondition.getOptions &&
            subcondition.key &&
            !subconditionOptions[subcondition.key]
          ) {
            subcondition.getOptions(forId, (options) =>
              setSubconditionOptions((prev) => ({
                ...prev,
                [subcondition.key]: options,
              })),
            );
          }
        });
      }
    } else {
      recipientConditions.forEach((c) => {
        const cond = standardConditions.find((sc) => sc.value === c.type);
        if (cond?.subConditions) {
          cond.subConditions.forEach((subcondition) => {
            if (
              subcondition.getOptions &&
              subcondition.key &&
              !subconditionOptions[subcondition.key]
            ) {
              subcondition.getOptions(forId, (options) =>
                setSubconditionOptions((prev) => ({
                  ...prev,
                  [subcondition.key]: options,
                })),
              );
            }
          });
        }
      });
    }
  }, [
    standardConditions,
    subconditionOptions,
    forId,
    recipientConditions,
    announcement.type,
    announcement.sendWhen,
    automaticConditions,
  ]);

  const [sendDisabled, sendDisabledReason] = useMemo(() => {
    const hasSendAt =
      announcement.sendAt !== undefined &&
      announcement.sendAt !== null &&
      announcement.sendAt !== "";

    if (util.isEmpty(announcement.subject))
      return [true, "Please fill in the announcement subject and body."];
    if (util.isEmpty(announcement.body))
      return [true, "Please fill in the announcement subject and body."];
    if (announcement.type === "automatic") {
      if (!announcement.sendWhen) {
        return [true, "At least one automation rule is required."];
      }
    } else {
      if (
        standardConditionsRequired &&
        standardConditions?.length &&
        recipientConditions.length === 0
      ) {
        return [true, "Announcements must be sent to at least one user."];
      }
      if (
        (announcement.type === "scheduled" || hasSendAt) &&
        !isValid(new Date(announcement.sendAt))
      ) {
        return [true, "Please select a valid send date and time."];
      }
    }
    if (announcement.calendarEvent) {
      if (!isValid(new Date(announcement.calendarEvent.startDate))) {
        return [
          true,
          "Please select a valid calendar event start date and time.",
        ];
      }
      if (!isValid(new Date(announcement.calendarEvent.endDate))) {
        return [
          true,
          "Please select a valid calendar event end date and time.",
        ];
      }
      if (!announcement.calendarEvent.organiserName) {
        return [true, "Please enter a calendar event organiser name."];
      }
      if (!announcement.calendarEvent.organiserEmail) {
        return [true, "Please enter a calendar event organiser email."];
      }
      if (!announcement.calendarEvent.name) {
        return [true, "Please enter a calendar event name."];
      }
    }

    return [false, null];
  }, [
    announcement,
    standardConditions,
    recipientConditions,
    standardConditionsRequired,
  ]);

  const sendAtValid = useMemo(
    () =>
      announcement.sendAt !== undefined &&
      announcement.sendAt !== null &&
      announcement.sendAt !== "" &&
      isValid(new Date(announcement.sendAt)),
    [announcement.sendAt],
  );
  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!util.isEmpty(announcement)}
      onClose={() => setAnnouncement({})}
    >
      <Modal.Header>
        {announcement._id ?
          t("announcements.automatic.edit.title")
        : <>
            {localStorage.getItem(`announcement-${forId}`) ?
              t("announcements.manual.continue.title")
            : t("announcements.automatic.new.title")}
            <br />
            <small>{t("announcements.manual.sendInfo")}</small>
          </>
        }
      </Modal.Header>
      <Modal.Content scrolling>
        <Segment>
          <h3>Announcement type</h3>
          {(
            (announcement._id &&
              (announcement.type === "standard" ||
                announcement.type === "scheduled")) ||
            !announcement._id
          ) ?
            <>
              <RadioHeader
                header="Standard"
                as="h4"
                content="Send or schedule announcements to a custom audience"
                value="standard"
                checked={
                  announcement.type === "standard" ||
                  announcement.type === "scheduled"
                }
                onChange={() =>
                  setAnnouncement((prev) => ({ ...prev, type: "standard" }))
                }
              />
              {(announcement.type === "standard" ||
                announcement.type === "scheduled") && (
                <div
                  style={{
                    marginLeft: 30,
                    marginBottom: 20,
                    borderLeft: "1px solid #d3cfcf",
                    paddingLeft: 20,
                  }}
                >
                  {standardConditions?.length ?
                    <>
                      <h5>{t("announcements.manual.recipients.title")}</h5>
                      <p>{t("announcements.manual.recipients.info")}</p>
                      {standardConditionsRequired ? null : (
                        <p>
                          {t(
                            "announcements.manual.recipients.condition.optional",
                            { type: forType },
                          )}
                        </p>
                      )}
                      <h5 style={{ fontSize: "small", marginBottom: 5 }}>
                        Recipient conditions{" "}
                        {standardConditionsRequired ?
                          <Icon
                            name="asterisk"
                            style={{
                              position: "relative",
                              top: -5,
                              marginLeft: 3,
                            }}
                            color="red"
                            size="tiny"
                          />
                        : null}
                      </h5>
                      {recipientConditions.map((c, i) => {
                        const subconditions = getStandardSubconditions(c);

                        return (
                          <>
                            <Grid stackable key={c.value}>
                              <Grid.Column computer={8}>
                                <Select
                                  placeholder={t(
                                    "announcements.manual.recipients.condition.select",
                                  )}
                                  fluid
                                  options={standardConditions.map((s) => ({
                                    ...s,
                                    key: s.value,
                                    text: t(s.text),
                                  }))}
                                  onChange={(_e, s) => {
                                    updateCondition(i, "type", s.value);
                                  }}
                                  value={c.type}
                                />
                              </Grid.Column>
                              <Grid.Column computer={6}>
                                {subconditions?.map((subcondition) => {
                                  return (
                                    <>
                                      {subcondition.type === "labels" ?
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                          }}
                                        >
                                          <div>
                                            <LabelChooser
                                              challengeId={
                                                forType === "challenge" ? forId
                                                : undefined
                                              }
                                              trigger={
                                                <Button
                                                  icon="bookmark"
                                                  content="Add labels"
                                                  basic
                                                />
                                              }
                                              onComplete={(em) =>
                                                updateSubconditionOptions(
                                                  c.key,
                                                  em,
                                                  false,
                                                )
                                              }
                                              onRemoveExisting={(em) =>
                                                updateSubconditionOptions(
                                                  c.key,
                                                  em,
                                                  false,
                                                )
                                              }
                                              existing={c.data}
                                            />
                                          </div>
                                          <div>
                                            {c.data?.map((label) => (
                                              <LabelItem
                                                key={label}
                                                label={label}
                                                onRemove={(label) =>
                                                  updateSubconditionOptions(
                                                    c.key,
                                                    label,
                                                    false,
                                                  )
                                                }
                                              />
                                            ))}
                                          </div>
                                        </div>
                                      : null}
                                      {subcondition.type === "dropdown" ?
                                        <Select
                                          placeholder={
                                            subcondition.placeholder ??
                                            "Select an option"
                                          }
                                          fluid
                                          options={(
                                            subconditionOptions[
                                              subcondition.key
                                            ] ?? []
                                          ).map((s) => ({
                                            key: s._id,
                                            value: s._id,
                                            text: s.name,
                                          }))}
                                          onChange={(_e, s) =>
                                            updateSubconditionOptions(
                                              c.key,
                                              s.value,
                                              true,
                                            )
                                          }
                                          value={c.data}
                                        />
                                      : null}
                                      {subcondition.type === "date" ?
                                        <DateInput
                                          placeholder="Date"
                                          value={
                                            c.data ?
                                              formatDate(c.data, "yyyy-MM-dd")
                                            : undefined
                                          }
                                          dateFormat="yyyy-MM-dd"
                                          iconPosition="left"
                                          closable
                                          onChange={(_e, { value }) => {
                                            const parsed = parse(
                                              value as string,
                                              "yyyy-MM-dd",
                                              new Date(),
                                            );
                                            updateSubconditionOptions(
                                              c.key,
                                              isValid(parsed) ?
                                                parsed.toISOString()
                                              : undefined,
                                              true,
                                            );
                                          }}
                                        />
                                      : null}
                                    </>
                                  );
                                })}
                              </Grid.Column>
                              <Grid.Column
                                computer={2}
                                textAlign="center"
                                style={{ marginTop: 5 }}
                              >
                                <Button
                                  size="mini"
                                  basic
                                  icon="trash"
                                  onClick={() => deleteCondition(i)}
                                />
                              </Grid.Column>
                            </Grid>
                            {i !== recipientConditions.length - 1 ?
                              <Divider horizontal>And</Divider>
                            : null}
                          </>
                        );
                      })}
                      <Button
                        size="small"
                        icon="plus"
                        content={t(
                          "announcements.manual.recipients.condition.add",
                        )}
                        basic
                        onClick={addCondition}
                        style={{ marginTop: 10 }}
                      />
                      {recipientsMatched === 0 ?
                        <h4 style={{ marginTop: 20 }}>
                          <Icon name="exclamation triangle" />{" "}
                          {t("announcements.manual.recipients.none")}
                        </h4>
                      : <h4 style={{ marginTop: 20 }}>
                          This announcement will be sent to {recipientsMatched}{" "}
                          users.
                        </h4>
                      }
                    </>
                  : <h3>{t("announcements.manual.recipients.all")}</h3>}
                </div>
              )}
            </>
          : null}
          {enabledAnnouncementTypes.includes("automatic") &&
            (announcement.type === "automatic" || !announcement._id) && (
              <div>
                <RadioHeader
                  header="Automatic"
                  content="Configure announcements to be sent automatically based on user actions"
                  value="automatic"
                  as="h4"
                  checked={announcement.type === "automatic"}
                  onChange={() =>
                    setAnnouncement((prev) => ({ ...prev, type: "automatic" }))
                  }
                />
                {announcement.type === "automatic" && (
                  <div
                    style={{
                      marginLeft: 30,
                      borderLeft: "1px solid #d3cfcf",
                      paddingLeft: 20,
                    }}
                  >
                    <h5>Automation rule</h5>
                    Please choose which condition must be met to send this
                    announcement.
                    <Divider hidden />
                    <Form>
                      <Form.Select
                        style={{ display: "block" }}
                        label={t("announcements.automatic.list.sendWhen")}
                        placeholder="Select an event trigger"
                        required
                        options={automaticConditions.map((c) => ({
                          key: c.value,
                          text: t(c.text),
                          value: c.value,
                        }))}
                        value={announcement.sendWhen}
                        onChange={(_e, { value }) => {
                          setAnnouncement((prevState) => ({
                            ...prevState,
                            sendWhen: value,
                          }));
                        }}
                      />
                      {announcement.sendWhen === "submitIdeaWithConditions" ?
                        <>
                          <p>
                            Only send this announcement if{" "}
                            <strong>
                              <Dropdown
                                inline
                                value={
                                  announcement.sendWhenConditions
                                    ?.conditionType ?? "any"
                                }
                                options={[
                                  { key: "any", text: "any of", value: "any" },
                                  { key: "all", text: "all of", value: "all" },
                                ]}
                                onChange={(_event, { value }) => {
                                  setAnnouncement((prevState) => ({
                                    ...prevState,
                                    sendWhenConditions: {
                                      ...(prevState.sendWhenConditions || {}),
                                      conditionType: value,
                                    },
                                  }));
                                }}
                              />
                            </strong>{" "}
                            the following conditions are met:
                          </p>
                          {announcement.sendWhenConditions?.conditions?.map(
                            (condition, i) => (
                              <>
                                <Form.Group
                                  widths="equal"
                                  key={`${i}-${condition.field}-${condition.condition}`}
                                >
                                  <Form.Field>
                                    {i === 0 ?
                                      <label htmlFor={`condition-${i}`}>
                                        1. Field with name
                                        {condition.field &&
                                          !subconditionOptions.ideaFields
                                            ?.filter(
                                              (f) => f.type in conditionChecks,
                                            )
                                            ?.map((f) => f.id)
                                            .includes(condition.field) && (
                                            <Popup
                                              trigger={
                                                <Icon
                                                  name="warning circle"
                                                  color="red"
                                                  style={{ marginLeft: 5 }}
                                                />
                                              }
                                              content={`This announcement condition is invalid because the specified field is not present in the ${t("generic.challenge")} ${t("generic.idea")} format.`}
                                            />
                                          )}
                                      </label>
                                    : null}
                                    <Form.Dropdown
                                      placholder={subconditions[0].placeholder}
                                      clearable
                                      selection
                                      value={condition.field}
                                      options={subconditionOptions.ideaFields
                                        ?.filter(
                                          (f) => f.type in conditionChecks,
                                        )
                                        ?.map((f) => ({
                                          key: f.id,
                                          description: f.section,
                                          text: f.title,
                                          value: f.id,
                                        }))}
                                      onChange={(_e, { value }) => {
                                        setAnnouncement((prevState) => ({
                                          ...prevState,
                                          sendWhenConditions: {
                                            ...(prevState.sendWhenConditions ||
                                              {}),
                                            conditions:
                                              prevState.sendWhenConditions?.conditions?.map(
                                                (c, j) => {
                                                  if (i === j) {
                                                    return {
                                                      ...c,
                                                      field: value,
                                                      condition:
                                                        getDefaultCondition(
                                                          subconditionOptions.ideaFields?.find(
                                                            (f) =>
                                                              f.id === value,
                                                          )?.type,
                                                        ),
                                                      value: null,
                                                    };
                                                  }
                                                  return c;
                                                },
                                              ),
                                          },
                                        }));
                                      }}
                                    />
                                  </Form.Field>
                                  <Form.Field>
                                    {i === 0 ?
                                      <label htmlFor={`condition-${i}`}>
                                        2. Meets condition
                                      </label>
                                    : null}
                                    <Form.Dropdown
                                      placeholder="Select a condition"
                                      selection
                                      value={condition.condition}
                                      options={
                                        availableConditions[
                                          subconditionOptions.ideaFields?.find(
                                            (chosenField) =>
                                              chosenField.id ===
                                              condition.field,
                                          )?.type
                                        ]
                                      }
                                      onChange={(_e, { value }) => {
                                        setAnnouncement((prevState) => ({
                                          ...prevState,
                                          sendWhenConditions: {
                                            ...(prevState.sendWhenConditions ||
                                              {}),
                                            conditions:
                                              prevState.sendWhenConditions?.conditions?.map(
                                                (c, j) => {
                                                  if (i === j) {
                                                    return {
                                                      ...c,
                                                      condition: value,
                                                      value: null,
                                                    };
                                                  }
                                                  return c;
                                                },
                                              ),
                                          },
                                        }));
                                      }}
                                    />
                                  </Form.Field>
                                  <Form.Field>
                                    {i === 0 ?
                                      <label htmlFor={`condition-${i}`}>
                                        2. Meets condition
                                      </label>
                                    : null}
                                    {renderValueCheck(
                                      condition,
                                      subconditionOptions.ideaFields?.find(
                                        (chosenField) =>
                                          chosenField.id === condition.field,
                                      ),
                                      (value) => {
                                        setAnnouncement((prevState) => ({
                                          ...prevState,
                                          sendWhenConditions: {
                                            ...(prevState.sendWhenConditions ||
                                              {}),
                                            conditions:
                                              prevState.sendWhenConditions?.conditions?.map(
                                                (c, j) => {
                                                  if (i === j) {
                                                    return { ...c, value };
                                                  }
                                                  return c;
                                                },
                                              ),
                                          },
                                        }));
                                      },
                                    )}
                                  </Form.Field>
                                  <Button
                                    style={{
                                      marginTop: i === 0 ? 25 : 0,
                                      maxHeight: "fit-content",
                                    }}
                                    icon="trash"
                                    onClick={() =>
                                      setAnnouncement((prevState) => ({
                                        ...prevState,
                                        sendWhenConditions: {
                                          ...(prevState.sendWhenConditions ||
                                            {}),
                                          conditions:
                                            prevState.sendWhenConditions?.conditions?.filter(
                                              (_c, j) => j !== i,
                                            ),
                                        },
                                      }))
                                    }
                                  />
                                </Form.Group>
                              </>
                            ),
                          )}
                          <Form.Group>
                            <Form.Button
                              onClick={() => {
                                setAnnouncement((prevState) => ({
                                  ...prevState,
                                  sendWhenConditions: {
                                    ...(prevState.sendWhenConditions || {}),
                                    conditions: [
                                      ...(prevState.sendWhenConditions
                                        ?.conditions ?? []),
                                      {
                                        field: null,
                                        condition: "isNotEmpty",
                                        value: null,
                                      },
                                    ],
                                  },
                                }));
                              }}
                            >
                              Add a condition
                            </Form.Button>
                          </Form.Group>
                        </>
                      : null}
                      {announcement.sendWhen === "moveToLane" ?
                        <>
                          <p>Select a lane</p>
                          <Select
                            value={
                              announcement.sendWhenConditions?.conditions?.[0]
                                ?.value ||
                              announcement.sendWhenConditions?.conditions?.[0]
                                ?.laneId
                            }
                            onChange={(_e, { value }) => {
                              setAnnouncement((prevState) => ({
                                ...prevState,
                                sendWhenConditions: {
                                  ...(prevState.sendWhenConditions || {}),
                                  conditions: [{ laneId: value }],
                                },
                              }));
                            }}
                            options={subconditionOptions.moveToLane}
                            placeholder="Select a lane"
                          />
                        </>
                      : null}
                      {(
                        announcement.sendWhen ===
                        "submittedAllAssessmentsForChallenge"
                      ) ?
                        <Select
                          value={
                            announcement.sendWhenConditions?.conditions?.[0]
                              ?.assessmentRoundId || "legacy"
                          }
                          onChange={(_e, { value }) => {
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              sendWhenConditions: {
                                ...(prevState.sendWhenConditions || {}),
                                conditions: [{ assessmentRoundId: value }],
                              },
                            }));
                          }}
                          options={subconditionOptions.assessmentRounds}
                          placeholder="Select an assessment"
                        />
                      : null}
                    </Form>
                  </div>
                )}
              </div>
            )}
        </Segment>
        {announcement.countSent ?
          <p>
            This announcement has been sent {announcement.countSent || 0} times.
          </p>
        : null}
        <AnnouncementBase
          forType={forType}
          forId={forId}
          announcement={announcement}
          setAnnouncement={setAnnouncement}
        />
        {announcement.type !== "automatic" && (
          <Message
            icon="info"
            info
            size="small"
            header={`Don't use ${util.appName()} to send out marketing messages`}
            content={
              "For GDPR purposes, announcements should be used only for platform activity and not for advertising events, services or other marketing correspondence."
            }
          />
        )}
      </Modal.Content>

      <Modal.Actions>
        {announcement.type !== "automatic" && (
          <span>
            {t("announcements.form.info", { audience: recipientsMatched })}
          </span>
        )}
        <Button
          style={{ marginLeft: 16 }}
          content={t("generic.cancel")}
          onClick={() => setAnnouncement({})}
        />
        {announcement._id ?
          <TooltipButton
            secondary
            icon="save"
            content={t("generic.saveChanges")}
            onClick={updateAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            tooltip={sendDisabledReason}
          />
        : announcement.type !== "automatic" && announcement.sendAt ?
          <TooltipButton
            secondary
            icon="calendar"
            content={
              sendAtValid ?
                `Send on ${formatDate(announcement.sendAt, "MMMM do yyyy 'at' HH:mm")}`
              : "Invalid date entered"
            }
            onClick={createAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            tooltip={sendDisabledReason}
          />
        : <TooltipButton
            secondary
            icon={announcement.type === "standard" ? "send" : "plus"}
            content={
              announcement.type === "standard" ?
                t("announcements.form.send")
              : "Create announcement"
            }
            onClick={createAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            style={{ marginLeft: "0" }}
            tooltip={sendDisabledReason}
          />
        }
      </Modal.Actions>
      <Modal.Actions>
        <TooltipButton
          content="Preview email"
          tooltip={sendDisabledReason}
          basic
          size="tiny"
          icon="eye"
          onClick={() => setEmailPreviewOpen(true)}
          disabled={sendDisabled}
        />
        {!announcement._id && (
          <Button
            icon="trash"
            size="tiny"
            content={t("generic.delete")}
            basic
            onClick={() => {
              setAnnouncement({});
              removeLocalStorageItem(`announcement-${forId}`);
            }}
          />
        )}
        {announcement._id ?
          <>
            {copyToNew ?
              <Button
                basic
                size="tiny"
                icon="copy"
                content="Copy to new"
                onClick={() => {
                  setAnnouncement({});
                  window.scrollTo(0, 0);
                  setTimeout(() => {
                    copyToNew(announcement);
                  }, 300);
                }}
              />
            : null}
            <Button
              onClick={() => {
                deleteAnnouncement(announcement._id, () => {
                  setAnnouncement({});
                });
              }}
              content={t("generic.delete")}
              icon="trash"
              basic
              size="small"
            />
          </>
        : null}
      </Modal.Actions>
    </Modal>
  );
};

export default AnnouncementModal;
